import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import { DataGridPagination } from './DataGridPagination';
import { BulkAction, DataTableProps } from './table/types';
import { useDataTableState } from './table/useDataTableState';
import { useDataTableColumns } from './table/useDataTableColumns';
import { DataTableToolbar } from './table/DataTableToolbar';
import { DataTableGridRenderer } from './table/DataTableGridRenderer';

export type { BulkAction, DataTableProps } from './table/types';

export function DataTable<T extends Record<string, any>>(props: DataTableProps<T>) {
  const {
    data,
    columns,
    isLoading = false,
    isRefreshing = false,
    onRefresh,
    unfilteredTotalRows,
    enableSelection = false,
    getRowId = (row: T, idx: number) => (row.id !== undefined ? String(row.id) : String(idx)),
    onRowClick,
    canEdit = false,
    onCellCommit,
    primaryAction,
    customToolbarActions,
    bulkActions,
    emptyTitle = 'No records found',
    emptySubtitle = 'Try adjusting your search or filters to find what you are looking for.',
    emptyState,
    onServerSortChange,
    onServerFilterChange,
    onServerSearchChange,
  } = props;

  const state = useDataTableState(props);

  const { tanstackColumns, visibilityColumnsList } = useDataTableColumns({
    columns,
    enableSelection,
    canEdit,
    getRowId,
    activeCell: state.activeCell,
    editingCell: state.editingCell,
    setEditingCell: state.setEditingCell,
    onCellCommit,
  });

  const table = useReactTable({
    data: state.paginatedData,
    columns: tanstackColumns,
    state: {
      sorting: state.sorting,
      columnVisibility: state.columnVisibility,
      rowSelection: state.rowSelection,
    },
    onSortingChange: state.handleSortingChange,
    onColumnVisibilityChange: state.setColumnVisibility,
    onRowSelectionChange: state.handleRowSelectionChange,
    getRowId,
    manualPagination: state.isServerSide,
    manualSorting: state.isServerSide && !!onServerSortChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: state.isServerSide && !!onServerSortChange ? undefined : getSortedRowModel(),
    enableRowSelection: true,
  });

  return (
    <div className="flex flex-col h-full w-full min-w-0 max-w-full bg-cf-surface text-cf-textPrimary border border-cf-border rounded-lg overflow-hidden select-none">
      {/* 1. TOP TOOLBAR */}
      <DataTableToolbar
        columns={columns}
        activeFilterRules={state.activeFilterRules}
        onFilterRulesChange={state.handleFilterRulesChange}
        globalFilter={state.globalFilter}
        onSearchChange={state.setGlobalFilter}
        searchInputRef={state.searchInputRef}
        visibilityColumnsList={visibilityColumnsList}
        columnVisibility={state.columnVisibility}
        setColumnVisibility={state.setColumnVisibility}
        selectedRowIds={state.selectedRowIds}
        onClearSelection={() => state.handleRowSelectionChange({})}
        bulkActions={bulkActions}
        selectedRows={state.selectedRows}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        isLoading={isLoading}
        density={state.density}
        setDensity={state.setDensity}
        exportOpen={state.exportOpen}
        setExportOpen={state.setExportOpen}
        isExporting={state.isExporting}
        exportButtonRef={state.exportButtonRef}
        onExport={state.handleExport}
        customToolbarActions={customToolbarActions}
        primaryAction={primaryAction}
      />

      {/* 2. MAIN SPREADSHEET TABLE (Sideways Scrollable + Sticky ID/Actions) */}
      <DataTableGridRenderer
        table={table}
        columns={columns}
        paginatedData={state.paginatedData}
        isMobile={state.isMobile}
        rowHeightClass={state.rowHeightClass}
        activeFilterRules={state.activeFilterRules}
        openFilterColId={state.openFilterColId}
        setOpenFilterColId={state.setOpenFilterColId}
        handleColumnFilterChange={state.handleColumnFilterChange}
        handleColumnSort={state.handleColumnSort}
        onRowClick={onRowClick}
        setActiveCell={state.setActiveCell}
        setEditingCell={state.setEditingCell}
        canEdit={canEdit}
        isLoading={isLoading}
        emptyState={emptyState}
        emptyTitle={emptyTitle}
        emptySubtitle={emptySubtitle}
      />

      {/* 3. PAGINATION FOOTER */}
      <DataGridPagination
        page={state.page}
        pageSize={state.pageSize}
        totalRows={unfilteredTotalRows ?? state.totalRows}
        filteredCount={state.totalRows}
        isFiltered={
          state.isServerSide
            ? (unfilteredTotalRows !== undefined && state.totalRows < unfilteredTotalRows) ||
              Boolean(state.globalFilter.trim()) ||
              state.activeFilterRules.length > 0
            : (!onServerFilterChange && state.activeFilterRules.length > 0) ||
              (!onServerSearchChange && Boolean(state.globalFilter.trim()))
        }
        onPageChange={state.handlePageChange}
        onPageSizeChange={state.handlePageSizeChange}
        selectedCount={state.selectedRowIds.length}
      />
    </div>
  );
}
